import { useEffect, useRef, useState } from 'react';
import { AppState, Keyboard, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { requireOptionalNativeModule } from 'expo';
import { getActiveRouteInfo, useNavState } from '../../contexts/NavigationStateContext';
import { parseStoreReviewHistory, recordActiveDay, storeReviewDue, type StoreReviewHistory } from '../../services/feedback/storeReviewPolicy';

type NativeStoreReview = {
  isAvailableAsync(): Promise<boolean>;
  requestReview(): Promise<void>;
};

export default function useStoreReview(userId: string, scope: string, available: boolean, blocked: boolean) {
  const navState = useNavState();
  const [foreground, setForeground] = useState(AppState.currentState === 'active');
  const [history, setHistory] = useState<{ scope: string; value: StoreReviewHistory } | null>(null);
  const [returnedHome, setReturnedHome] = useState(false);
  const previousRoute = useRef<{ scope: string; tab: string | null } | null>(null);
  const requesting = useRef(false);
  const enabled = available && !!userId && (Platform.OS === 'ios' || Platform.OS === 'android');
  const version = Constants.expoConfig?.version || '';
  // Per person on this installation: switching care accounts cannot reset the
  // cooldown. Reinstallation starts the observation period from zero.
  const storageKey = `@eleplan/store-review/${userId}`;

  useEffect(() => {
    if (!enabled) return;
    const listener = AppState.addEventListener('change', state => setForeground(state === 'active'));
    setForeground(AppState.currentState === 'active');
    return () => listener.remove();
  }, [enabled]);

  useEffect(() => {
    setHistory(null);
    if (!enabled || !foreground) return;
    let active = true;
    // Opening a notification and immediately leaving is not a day of use.
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const raw = await AsyncStorage.getItem(storageKey);
          if (!active) return;
          const next = recordActiveDay(raw ? parseStoreReviewHistory(raw) : null, Date.now());
          await AsyncStorage.setItem(storageKey, JSON.stringify(next));
          if (active) setHistory({ scope, value: next });
        } catch { /* Unreadable frequency limits suppress the prompt. */ }
      })();
    }, 60_000);
    return () => { active = false; clearTimeout(timer); };
  }, [enabled, foreground, scope, storageKey]);

  useEffect(() => {
    const { isLoggedIn, activeTab } = getActiveRouteInfo(navState);
    const previous = previousRoute.current;
    previousRoute.current = { scope, tab: isLoggedIn ? activeTab : null };
    const returned = previous?.scope === scope && (previous.tab === 'Plans' || previous.tab === 'AI')
      && isLoggedIn && activeTab === 'Home';
    setReturnedHome(enabled && foreground && !blocked && !!returned);
  }, [navState, enabled, foreground, scope, blocked]);

  useEffect(() => {
    if (!enabled || !foreground || blocked || !returnedHome || history?.scope !== scope
      || !storeReviewDue(history.value, version, Date.now())) return;
    let active = true;
    const safeToPrompt = () => active && AppState.currentState === 'active' && !Keyboard.isVisible();
    const timer = setTimeout(() => {
      if (requesting.current || !safeToPrompt()) return;
      requesting.current = true;
      void (async () => {
        try {
          // Older installed binaries may not contain ExpoStoreReview yet.
          // Optional lookup also avoids an external-store redirect fallback.
          const review = requireOptionalNativeModule<NativeStoreReview>('ExpoStoreReview');
          if (!review || !await review.isAvailableAsync() || !safeToPrompt()) return;
          const raw = await AsyncStorage.getItem(storageKey);
          if (!raw || !safeToPrompt()) return;
          const latest = parseStoreReviewHistory(raw);
          if (!storeReviewDue(latest, version, Date.now())) return;
          const next = { ...latest, lastAttempt: Date.now(), lastVersion: version };
          // Count the attempt before calling the OS. Neither platform tells us
          // whether a dialog appeared, was dismissed, or a rating was submitted.
          await AsyncStorage.setItem(storageKey, JSON.stringify(next));
          if (!safeToPrompt()) return;
          await review.requestReview();
        } catch { /* Reviews are optional; never interrupt the app with an error. */ }
        finally { requesting.current = false; if (active) setReturnedHome(false); }
      })();
    }, 3_000);
    return () => { active = false; clearTimeout(timer); };
  }, [enabled, foreground, blocked, returnedHome, history, scope, storageKey, version]);
}
